import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { User, Mail, ArrowRight, GraduationCap, Loader2 } from 'lucide-react';
import { getCapacitaciones } from '../lib/api';

export function StepUserData({
  userData,
  onUserDataChange,
  onNext,
  capacitacionId,
  onCapacitacionChange,
  resumePasswordRequired = false,
  resumePassword = '',
  onResumePasswordChange,
  lockedIdentity = null,
}) {
  const [capacitaciones, setCapacitaciones] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadCapacitaciones();
  }, []);

  const loadCapacitaciones = async () => {
    try {
      const data = await getCapacitaciones();
      setCapacitaciones(data);
    } catch (error) {
      console.error('Error loading capacitaciones:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Validation: name, email AND capacitacion (if there are capacitaciones available)
  const isValid = 
    userData.name?.trim().length >= 2 && 
    userData.email?.includes('@') && 
    (capacitaciones.length === 0 || capacitacionId) &&
    (!resumePasswordRequired || resumePassword.length >= 8);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isValid) {
      onNext();
    }
  };

  return (
    <Card className="max-w-xl mx-auto">
      <CardHeader className="text-center">
        <CardTitle className="text-2xl">Bienvenido/a a tu Planificador de Vida</CardTitle>
        <CardDescription>
          Comencemos con tus datos básicos. Usa el email con el que estás registrado/a en la academia. Si ya habías empezado este formulario con ese email, vas a continuar desde donde lo dejaste.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {lockedIdentity && (
            <div className="rounded-lg border border-emerald-700/50 bg-emerald-950/20 p-4">
              <p className="text-sm font-semibold text-emerald-500">
                Cuenta vinculada con la academia
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                Usaremos los datos de tu sesión de Learn. No necesitás crear
                otra contraseña para completar el Planificador.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="name" className="flex items-center gap-2">
              <User className="w-4 h-4" />
              Nombre Completo
            </Label>
            <Input
              id="name"
              value={userData.name || ''}
              onChange={(e) => onUserDataChange({ ...userData, name: e.target.value })}
              placeholder="Tu nombre completo"
              required
              minLength={2}
              readOnly={Boolean(lockedIdentity)}
              aria-readonly={Boolean(lockedIdentity)}
              data-testid="personal-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email
            </Label>
            <Input
              id="email"
              type="email"
              value={userData.email || ''}
              onChange={(e) => onUserDataChange({ ...userData, email: e.target.value })}
              placeholder="tu@email.com"
              required
              readOnly={Boolean(lockedIdentity)}
              aria-readonly={Boolean(lockedIdentity)}
              data-testid="personal-email-input"
            />
            <p className="text-xs text-muted-foreground">
              {lockedIdentity
                ? 'Este email viene de tu cuenta de Academia Cortex.'
                : 'Usa el email registrado en Academia Cortex'}
            </p>
          </div>

          {resumePasswordRequired && (
            <div className="space-y-2 rounded-md border border-cyan-200 bg-cyan-50/70 p-3 dark:border-cyan-900 dark:bg-cyan-950/20">
              <Label htmlFor="resume-password">Contraseña para continuar</Label>
              <Input
                id="resume-password"
                type="password"
                value={resumePassword}
                onChange={(e) => onResumePasswordChange?.(e.target.value)}
                placeholder="Tu contraseña"
                autoComplete="current-password"
                minLength={8}
                required
              />
              <p className="text-xs text-muted-foreground">
                Este email ya tiene un formulario guardado. Ingresá la contraseña que creaste para retomarlo.
              </p>
            </div>
          )}

          {/* Capacitación selector */}
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : capacitaciones.length > 0 ? (
            <div className="space-y-2">
              <Label htmlFor="capacitacion" className="flex items-center gap-2">
                <GraduationCap className="w-4 h-4" />
                Capacitación que estás realizando
                <span className="text-red-500">*</span>
              </Label>
              <Select
                value={capacitacionId || ''}
                onValueChange={onCapacitacionChange}
              >
                <SelectTrigger data-testid="capacitacion-select">
                  <SelectValue placeholder="Seleccioná tu capacitación" />
                </SelectTrigger>
                <SelectContent>
                  {capacitaciones.map((cap) => (
                    <SelectItem key={cap._id} value={cap._id}>
                      {cap.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!capacitacionId && (
                <p className="text-xs text-amber-600">
                  Seleccioná la capacitación en la que estás participando
                </p>
              )}
            </div>
          ) : null}

          <Button 
            type="submit" 
            className="w-full" 
            disabled={!isValid}
            data-testid="wizard-next-button"
          >
            Comenzar
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

export default StepUserData;
